import { useCallback, useEffect, useId, useState, type FormEvent } from 'react'
import { useSelector } from 'react-redux'
import { Check, Copy, Plus, Trash2 } from 'lucide-react'
import type { RootState } from '../store/store'
import { login } from './UserMenu'
import './ApiTokenControls.css'

const NAME_MAX = 100

/** Public metadata from GET /api/api-tokens (never includes plaintext). */
interface ApiTokenInfo {
  id: string
  user_id: string
  name: string
  created_at: string
}

/** POST mint response — plaintext `token` shown once in local state only. */
interface MintedApiToken {
  token: string
  id: string
  name: string
  created_at: string
}

function errorMessage(data: unknown, fallback: string): string {
  if (data && typeof data === 'object' && 'error' in data && typeof (data as { error: unknown }).error === 'string') {
    return (data as { error: string }).error
  }
  return fallback
}

/** Mint / list / revoke UserApiKeys via CookiePrincipal `/api/api-tokens`. */
export default function ApiTokenControls() {
  const { user } = useSelector((s: RootState) => s.auth)
  return <UserApiTokenControls key={user?.id ?? 'signed-out'} user={user} />
}

function UserApiTokenControls({ user }: { user: RootState['auth']['user'] }) {
  const nameInputId = useId()
  const [tokens, setTokens] = useState<ApiTokenInfo[]>([])
  const [loading, setLoading] = useState(!!user)
  const [loadError, setLoadError] = useState(false)
  const [name, setName] = useState('')
  const [newToken, setNewToken] = useState<MintedApiToken | null>(null)
  const [copied, setCopied] = useState(false)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [revoking, setRevoking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadTokens = useCallback(async () => {
    if (!user) {
      setTokens([])
      setLoading(false)
      setLoadError(false)
      return
    }
    setLoading(true)
    setLoadError(false)
    try {
      const res = await fetch('/api/api-tokens')
      if (!res.ok) throw new Error('load failed')
      const data = (await res.json()) as { tokens?: ApiTokenInfo[] }
      setTokens(Array.isArray(data.tokens) ? data.tokens : [])
    } catch {
      setLoadError(true)
      setTokens([])
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    void loadTokens()
  }, [loadTokens])

  async function handleGenerate(event: FormEvent) {
    event.preventDefault()
    if (!user) {
      login(window.location.pathname)
      return
    }
    if (!name.trim() || creating || newToken) return
    setError(null)
    setCreating(true)
    try {
      const res = await fetch('/api/api-tokens', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      })
      const data: unknown = await res.json().catch(() => null)
      if (!res.ok) {
        setError(errorMessage(data, 'Unable to create a token. Please try again.'))
        return
      }
      const minted = data as MintedApiToken
      setNewToken(minted)
      setName('')
      setCopied(false)
      setConfirmDeleteId(null)
      await loadTokens()
    } catch {
      setError('Unable to create a token. Please try again.')
    } finally {
      setCreating(false)
    }
  }

  async function handleCopy() {
    if (!newToken) return
    try {
      await navigator.clipboard.writeText(newToken.token)
      setCopied(true)
      setError(null)
    } catch {
      setError('Unable to copy automatically. Select and copy the token above.')
    }
  }

  async function handleDelete(id: string) {
    if (!user || revoking) return
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id)
      return
    }
    setError(null)
    setRevoking(true)
    try {
      const res = await fetch('/api/api-tokens', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      })
      const data: unknown = await res.json().catch(() => null)
      if (!res.ok) {
        setError(errorMessage(data, 'Unable to revoke the token. Please try again.'))
        return
      }
      setConfirmDeleteId(null)
      if (newToken?.id === id) setNewToken(null)
      await loadTokens()
    } catch {
      setError('Unable to revoke the token. Please try again.')
    } finally {
      setRevoking(false)
    }
  }

  return (
    <div className="api-token-controls" data-testid="api-token-controls">
      {newToken && (
        <div className="api-token-controls__reveal" role="status">
          <p className="api-token-controls__reveal-label">
            Copy the token for “{newToken.name}” now. It won&apos;t be shown again.
          </p>
          <div className="api-token-controls__reveal-row">
            <code className="api-token-controls__value">{newToken.token}</code>
            <button
              type="button"
              className="api-token-controls__copy-btn"
              onClick={() => void handleCopy()}
              aria-label={copied ? 'Copied' : 'Copy token'}
            >
              {copied ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button
            type="button"
            className="api-token-controls__dismiss-btn"
            onClick={() => setNewToken(null)}
          >
            I&apos;ve copied the token
          </button>
        </div>
      )}

      {loading ? (
        <div className="api-token-controls__status" role="status">
          Loading tokens...
        </div>
      ) : loadError ? (
        <div className="api-token-controls__error" role="alert">
          Unable to load your tokens.{' '}
          <button type="button" className="api-token-controls__copy-btn" onClick={() => void loadTokens()}>
            Retry
          </button>
        </div>
      ) : user && tokens.length === 0 ? (
        <p className="api-token-controls__none">No API tokens yet.</p>
      ) : null}

      {tokens.length > 0 && (
        <ul className="api-token-controls__list" aria-label="API tokens">
          {tokens.map((token) => (
            <li className="api-token-controls__item" key={token.id}>
              <div className="api-token-controls__info">
                <strong className="api-token-controls__name">{token.name}</strong>
                <span className="api-token-controls__exists">
                  Created {new Date(token.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="api-token-controls__actions">
                {confirmDeleteId === token.id && (
                  <>
                    <span className="api-token-controls__warning">
                      Clients using this token will lose access.
                    </span>
                    <button
                      type="button"
                      className="api-token-controls__copy-btn"
                      onClick={() => setConfirmDeleteId(null)}
                      disabled={revoking}
                    >
                      Cancel
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className={`api-token-controls__delete-btn${
                    confirmDeleteId === token.id ? ' api-token-controls__delete-btn--confirm' : ''
                  }`}
                  onClick={() => void handleDelete(token.id)}
                  disabled={revoking}
                  aria-label={`${confirmDeleteId === token.id ? 'Confirm revoke' : 'Revoke'} ${token.name}`}
                >
                  <Trash2 size={16} strokeWidth={1.75} aria-hidden />
                  {revoking && confirmDeleteId === token.id
                    ? 'Revoking...'
                    : confirmDeleteId === token.id
                      ? 'Confirm revoke'
                      : 'Revoke'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="api-token-controls__error" role="alert">
          {error}
        </p>
      )}

      {!newToken && (
        <form className="api-token-controls__create" onSubmit={(e) => void handleGenerate(e)}>
          <label htmlFor={nameInputId}>Token name</label>
          <div className="api-token-controls__create-row">
            <input
              id={nameInputId}
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Work laptop or CI"
              maxLength={NAME_MAX}
              required
              disabled={creating}
              autoComplete="off"
            />
            <button
              type="submit"
              className="api-token-controls__generate-btn"
              disabled={creating || !name.trim()}
            >
              <Plus size={16} strokeWidth={1.75} aria-hidden />
              {creating ? 'Creating...' : user ? 'Create token' : 'Sign in to create'}
            </button>
          </div>
          <p className="api-token-controls__hint">
            Creating a key keeps your existing keys active. Paste it into the MCP config below as{' '}
            <code>Bearer lbak_…</code>.
          </p>
        </form>
      )}
    </div>
  )
}
